import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { BakingPanConverterClient } from './ui/BakingPanConverterClient';

interface BakingPanConverterProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const BakingPanConverter: React.FC<BakingPanConverterProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Baking Pan Size Converter"
            description="Adapt any recipe to your pan size. Calculate the exact scaling factor for round, square, rectangular, bundt, and muffin pans."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="baking-pan-converter"
        >
            <BakingPanConverterClient onRecipeScalerClick={() => setActiveTool('recipe-scaler')} />
            <BakingPanConverterContent />
        </ToolContainer>
    );
};

const BakingPanConverterContent: React.FC = () => (
    <div className="mt-8 space-y-12 pt-8 border-t border-brand-border">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Baking Pan Size Converter: Adapt Any Recipe</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Found the perfect cake recipe but don't have the right pan? Don't stress. Our <strong>Baking Pan Converter</strong> calculates the exact volume difference between pans. Whether swapping a <strong>9-inch round</strong> for an <strong>8-inch square</strong>, or scaling a large <strong>bundt cake</strong> into <strong>cupcakes</strong>, this tool gives you the precise <strong>scaling factor</strong> to adjust your ingredients. Bake confidently in any pan you own!
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Use
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Select Original Pan:</strong> Choose the shape and enter dimensions of the pan specified in your recipe.</li>
                    <li><strong>Select Your Pan:</strong> Choose the shape and enter dimensions of the pan you want to use.</li>
                    <li><strong>Get Conversion Factor:</strong> The tool calculates a multiplier (e.g., 1.5x).</li>
                    <li><strong>Scale Recipe:</strong> Multiply all ingredient amounts by this number.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title uppercase tracking-wide text-xs">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            How do I measure a cake pan size?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Always measure from <strong>inside edge to inside edge</strong>, excluding the rim. For depth, place a ruler straight up from the bottom.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
